import * as React from 'react';

type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: 'dark' | 'light';
  /** Flips between light and dark relative to the resolved mode. */
  toggleTheme: () => void;
};

const ThemeProviderContext = React.createContext<ThemeProviderState>({
  theme: 'system',
  setTheme: () => null,
  resolvedTheme: 'light',
  toggleTheme: () => null,
});

function systemTheme(): 'dark' | 'light' {
  if (typeof window === 'undefined') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

/**
 * Light/dark/system theme provider: persists the choice under `storageKey`
 * and applies the resolved
 * mode as a `dark`/`light` class on the document element.
 */
function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'health-ui-theme',
}: ThemeProviderProps): React.JSX.Element {
  const [theme, setThemeState] = React.useState<Theme>(() => {
    try {
      return (localStorage.getItem(storageKey) as Theme) || defaultTheme;
    } catch {
      return defaultTheme;
    }
  });
  const [resolvedTheme, setResolvedTheme] = React.useState<'dark' | 'light'>(
    () => (theme === 'system' ? systemTheme() : theme),
  );

  React.useEffect(() => {
    if (theme !== 'system') {
      setResolvedTheme(theme);
      return undefined;
    }
    setResolvedTheme(systemTheme());
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent): void => {
      setResolvedTheme(event.matches ? 'dark' : 'light');
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [theme]);

  React.useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = React.useCallback(
    (nextTheme: Theme): void => {
      try {
        localStorage.setItem(storageKey, nextTheme);
      } catch {
        // Ignore storage failures (private mode, blocked storage).
      }
      setThemeState(nextTheme);
    },
    [storageKey],
  );

  const value = React.useMemo<ThemeProviderState>(
    () => ({
      theme,
      setTheme,
      resolvedTheme,
      toggleTheme: () => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark'),
    }),
    [theme, setTheme, resolvedTheme],
  );

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

function useTheme(): ThemeProviderState {
  return React.useContext(ThemeProviderContext);
}

export { ThemeProvider, useTheme, type Theme };
